import { useQuery } from "@tanstack/react-query";
import { useUser } from "@clerk/nextjs";
import { useQuery as useConvexQuery } from "convex/react";
import { useMemo } from "react";
import { api } from "@/convex/_generated/api";
import type { Id } from "@/convex/_generated/dataModel";

interface Profile {
  profileId: Id<"creators"> | Id<"brands">;
  profileType: "creator" | "brand";
  isActive: boolean;
}

export interface CreatorProfile {
  _id: Id<"creators">;
  name: string;
  language: string[];
  country: string[];
  label: string[];
  category: string[];
  contacts: string[];
  visibility: boolean;
  description_short: string;
  description_long: string;
  full_name: string;
  gender: string;
  email: string;
  phone: string;
  currency: string;
  avg_price: string;
  socialStats: Record<string, number>;
  avatar?: string;
}

export interface BrandProfile {
  _id: Id<"brands">;
  name: string;
  creationTime?: number;
  category?: string[];
  country?: string[];
  industry?: string[];
  language?: string[];
  contacts?: string[];
  visibility: boolean;
  description_short?: string;
  description_long?: string;
  company_name?: string;
  business_email?: string;
  business_phone?: string;
  website?: string;
  currency?: string;
  logo_url?: string;
  company_size?: string;
  annual_budget?: number;
}

export interface UserProfileStore {
  userId: string;
  primaryProfileId: Id<"creators"> | Id<"brands"> | null;
  userType?: "brand" | "creator";
  currentProfile: {
    type: "creator" | "brand";
    data: CreatorProfile | BrandProfile;
  } | null;
  creatorProfile?: CreatorProfile;
  brandProfile?: BrandProfile;
}

export function useUserProfileStore() {
  const { user } = useUser();
  const userProfile = useConvexQuery(
    api.frontend.userProfiles.getUserProfile,
    user?.id ? { userId: user.id } : "skip"
  );

  const currentProfile = useMemo(() => {
    return userProfile?.profiles.find(
      (profile: Profile) => profile.isActive
    );
  }, [userProfile?.profiles]);

  const currentProfileType = currentProfile?.profileType;

  const creatorProfileData = useConvexQuery(
    api.frontend.creators.getCreatorProfile,
    currentProfileType === "creator" && currentProfile?.profileId
      ? { creatorId: currentProfile.profileId as Id<"creators"> }
      : "skip"
  );

  const brandProfileData = useConvexQuery(
    api.frontend.brands.getBrandProfile,
    currentProfileType === "brand" && currentProfile?.profileId
      ? { profileId: currentProfile.profileId as Id<"brands"> }
      : "skip"
  );

  const formattedBrandProfile = useMemo(() => {
    if (!brandProfileData) return null;
    
    return {
      _id: brandProfileData._id,
      name: brandProfileData.name || "",
      creationTime: brandProfileData.creationTime,
      category: brandProfileData.category || [],
      country: brandProfileData.country || [],
      industry: brandProfileData.industry || [],
      language: brandProfileData.language || [],
      contacts: brandProfileData.contacts || [],
      visibility: brandProfileData.visibility ?? true,
      description_short: brandProfileData.description_short || "",
      description_long: brandProfileData.description_long || "",
      company_name: brandProfileData.company_name || "",
      business_email: brandProfileData.business_email || "",
      business_phone: brandProfileData.business_phone || "",
      website: brandProfileData.website || "",
      currency: brandProfileData.currency || "",
      logo_url: brandProfileData.logo_url || "",
      company_size: brandProfileData.company_size || "",
      annual_budget:
        typeof brandProfileData.annual_budget === "number"
          ? brandProfileData.annual_budget
          : 0,
    } as BrandProfile;
  }, [brandProfileData]);

  const formattedCreatorProfile = useMemo(() => {
    if (!creatorProfileData) return null;
    
    return {
      _id: creatorProfileData._id,
      name: creatorProfileData.name,
      language: creatorProfileData.language || [],
      country: creatorProfileData.country || [],
      label: creatorProfileData.label || [],
      category: creatorProfileData.category || [],
      contacts: creatorProfileData.contacts || [],
      visibility: creatorProfileData.visibility || false,
      description_short: creatorProfileData.description_short || "",
      description_long: creatorProfileData.description_long || "",
      full_name: creatorProfileData.full_name || "",
      gender: creatorProfileData.gender || "",
      email: creatorProfileData.email || "",
      phone: creatorProfileData.phone || "",
      currency: creatorProfileData.currency || "",
      avg_price: creatorProfileData.avg_price || "",
      socialStats: creatorProfileData.socialStats || {},
      avatar: creatorProfileData.avatar?.image || "",
    } as CreatorProfile;
  }, [creatorProfileData]);

  const queryKey = useMemo(
    () => [
      "userProfileStore",
      user?.id,
      currentProfileType,
      currentProfile?.profileId,
      brandProfileData?._id,
      creatorProfileData?._id,
    ],
    [
      user?.id,
      currentProfileType,
      currentProfile?.profileId,
      brandProfileData?._id,
      creatorProfileData?._id,
    ]
  );

  return useQuery<UserProfileStore | null>({
    queryKey,
    queryFn: () => {
      if (!user?.id || !userProfile) {
        return null;
      }

      return {
        userId: userProfile.userId,
        primaryProfileId: userProfile.primaryProfileId || null,
        userType: currentProfileType,
        currentProfile:
          currentProfileType === "creator" && formattedCreatorProfile
            ? {
                type: "creator",
                data: formattedCreatorProfile,
              }
            : currentProfileType === "brand" && formattedBrandProfile
              ? {
                  type: "brand",
                  data: formattedBrandProfile,
                }
              : null,
        creatorProfile:
          currentProfileType === "creator"
            ? formattedCreatorProfile || undefined
            : undefined,
        brandProfile:
          currentProfileType === "brand"
            ? formattedBrandProfile || undefined
            : undefined,
      };
    },
    enabled: !!user?.id,
  });
}
